import { useEffect, useRef, useState } from "react";
import { Search, Loader2, MapPin } from "lucide-react";
import { Modal } from "./Modal";

/**
 * Reusable address block: search + 4 inline fields.
 *
 * Props:
 *  - value: { strasse, hausnummer, plz, ort, country_code }
 *  - onChange: (next) => void
 *  - search: async (query) => results[]
 *  - idPrefix: string used in data-testids
 *  - title: optional subtitle
 *  - searchPlaceholder: optional
 */
export const AddressBlock = ({
  value,
  onChange,
  search,
  idPrefix = "addr",
  searchPlaceholder = "z. B. Hauptstraße 79, Lohmar",
}) => {
  const set = (patch) => onChange({ ...value, ...patch });

  const [pendingPick, setPendingPick] = useState(null);

  const applyPick = (place) => {
    onChange({
      ...value,
      strasse: place.road || value.strasse,
      hausnummer: place.house_number || value.hausnummer,
      plz: place.postcode || value.plz,
      ort: place.city || value.ort,
      country_code: (place.country_code || "DE").toUpperCase(),
    });
  };

  const handlePick = (place) => {
    setPendingPick(place);
  };

  const confirmPending = () => {
    if (pendingPick) applyPick(pendingPick);
    setPendingPick(null);
  };

  const cancelPending = () => {
    setPendingPick(null);
  };

  return (
    <div>
      <AddressSearch
        idPrefix={idPrefix}
        onPick={handlePick}
        search={search}
        placeholder={searchPlaceholder}
      />
      <div className="grid sm:grid-cols-6 gap-5 mt-5">
        <Field
          id={`${idPrefix}-strasse`}
          label="Straße *"
          value={value.strasse}
          onChange={(v) => set({ strasse: v })}
          className="sm:col-span-4"
        />
        <Field
          id={`${idPrefix}-hausnummer`}
          label="Hausnr. *"
          value={value.hausnummer}
          onChange={(v) => set({ hausnummer: v })}
          inputMode="numeric"
          className="sm:col-span-2"
        />
        <Field
          id={`${idPrefix}-plz`}
          label="PLZ *"
          value={value.plz}
          onChange={(v) => set({ plz: v })}
          inputMode="numeric"
          className="sm:col-span-2"
        />
        <Field
          id={`${idPrefix}-ort`}
          label="Ort *"
          value={value.ort}
          onChange={(v) => set({ ort: v })}
          className="sm:col-span-4"
        />
      </div>
      {value.country_code && value.country_code !== "DE" && (
        <div
          data-testid={`${idPrefix}-country-hint`}
          className="mt-3 text-[12px] text-[#d90429] flex items-center gap-2"
        >
          <MapPin className="w-3.5 h-3.5" />
          Adresse außerhalb Deutschlands erkannt — Auslandskennzeichen{" "}
          <strong>{value.country_code}</strong> wird automatisch eingetragen.
        </div>
      )}

      <Modal
        open={Boolean(pendingPick)}
        title="Adresse bestätigen"
        testIdPrefix={`${idPrefix}-confirm`}
        onClose={cancelPending}
        onConfirm={confirmPending}
        confirmLabel="Übernehmen"
      >
        {pendingPick && (
          <>
            <p className="text-[13px] text-[#505a74] mb-4">
              Stimmt die folgende Adresse?
            </p>
            <div className="bg-[#fafaf7] border border-[#e8e6df] rounded-sm p-4">
              <div className="text-[15px] font-semibold text-[#0a1128]">
                {pendingPick.road} {pendingPick.house_number}
              </div>
              <div className="text-[14px] text-[#0a1128] mt-0.5">
                {pendingPick.postcode} {pendingPick.city}
              </div>
              <div className="text-[12px] text-[#505a74] mt-1">
                {pendingPick.country}
                {pendingPick.country_code &&
                  pendingPick.country_code !== "DE" && (
                    <span className="ml-1 text-[#d90429] font-semibold">
                      [{pendingPick.country_code}]
                    </span>
                  )}
              </div>
            </div>
            <p className="text-[12px] text-[#505a74] mt-4">
              Mit &quot;Übernehmen&quot; werden Straße, PLZ und Ort in die Felder
              unterhalb übertragen.
            </p>
          </>
        )}
      </Modal>
    </div>
  );
};

const Field = ({ id, label, value, onChange, type = "text", className = "", inputMode }) => {
  let pattern;
  if (inputMode === "numeric") pattern = "[0-9]*";
  else if (inputMode === "decimal") pattern = "[0-9.,]*";
  else if (inputMode === "tel") pattern = "[0-9+\\s\\-()/]*";
  return (
  <label className={`block ${className}`}>
    <span className="eyebrow text-[#505a74]">{label}</span>
    <input
      type={type}
      inputMode={inputMode}
      pattern={pattern}
      value={value || ""}
      data-testid={`form-input-${id}`}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
    />
  </label>
  );
};

const AddressSearch = ({ onPick, search, idPrefix, placeholder }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const onChange = (val) => {
    setQ(val);
    if (timer.current) clearTimeout(timer.current);
    if (val.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      setOpen(true);
      const data = await search(val);
      setResults(data);
      setLoading(false);
    }, 350);
  };

  return (
    <div ref={boxRef} className="relative">
      <span className="eyebrow text-[#505a74]">
        Adresse suchen (OpenStreetMap)
      </span>
      <div className="relative mt-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#505a74]" />
        <input
          type="text"
          data-testid={`${idPrefix}-search`}
          value={q}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="block w-full bg-white border border-[#e8e6df] rounded-sm pl-10 pr-10 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#505a74] animate-spin" />
        )}
      </div>
      {open && (
        <div
          data-testid={`${idPrefix}-results`}
          className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#e8e6df] rounded-sm shadow-lg max-h-80 overflow-auto"
        >
          {results.length === 0 && !loading && (
            <div className="px-4 py-3 text-[13px] text-[#505a74]">
              Keine Treffer — bitte Adresse manuell eintragen.
            </div>
          )}
          {results.map((r, i) => (
            <button
              type="button"
              key={i}
              data-testid={`${idPrefix}-result-${i}`}
              onClick={() => {
                onPick(r);
                setQ(r.display);
                setOpen(false);
              }}
              className="block w-full text-left px-4 py-3 border-b border-[#e8e6df] last:border-b-0 hover:bg-[#fafaf7] transition"
            >
              <div className="text-[14px] font-semibold text-[#0a1128]">
                {r.road} {r.house_number}, {r.postcode} {r.city}
              </div>
              <div className="text-[12px] text-[#505a74] mt-0.5">
                {r.country}
                {r.country_code && r.country_code !== "DE" && (
                  <span className="ml-1 text-[#d90429] font-semibold">
                    [{r.country_code}]
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-[#505a74]">
        Adresse auswählen, um Straße, PLZ und Ort automatisch zu übernehmen.
      </p>
    </div>
  );
};
